import React from "react";
import Lottie from "lottie-react";
import styled from "styled-components";

import lottie_loading from "./lottie_loading.json";
import { transferToNative } from "@src/lib/WebAppBridge";

interface AuthorityProps {
    icon?: string;
    alt?: string;
    textContent?: React.ReactNode;
    buttonText?: string;
    onButtonClick?: () => void;
    isChatbot?: boolean;
    title?: string;
    description?: string;
    hasSampleView?: boolean;
}

interface ChatAuthorityProps extends AuthorityProps {
    button2Text?: string;
    button2Click?: () => void;
    button2State?: boolean;
    chatbotUpgradeNotice?: boolean;
}

export const Loading = () => (
    <LottieLayout>
        <Lottie loop={true} animationData={lottie_loading} className="lottie" />
        AI가 열심히 분석중입니다.
    </LottieLayout>
);

export const LockedContent = ({ icon, textContent, buttonText, onButtonClick, isChatbot, hasSampleView = false }: AuthorityProps) => {
    const handleSampleViewClick = () => {
        transferToNative("","openSampleList");
    }   
    
    return (
    <LockBoxStyle>
        <div className="img_box">
            <img src={icon || ""} alt="lock" />
        </div>
        <div className="text">{textContent}</div>
       {hasSampleView && <NoticeStyle>
            <p>✨ AI 상세 분석 리포트</p>
            <p>📊 실시간 시세 비교</p>
            <p>🎯 맞춤 입찰 전략</p>
        </NoticeStyle>}
        <FlexStyle>
            {hasSampleView && <SampleViewButton onClick={handleSampleViewClick}>샘플 미리보기</SampleViewButton>}
            <div className="button" style={{ background: isChatbot ? "#000" : "#2275ee" }} onClick={onButtonClick}>
                {buttonText}
            </div>
        </FlexStyle>
    </LockBoxStyle>
    );
};

export const ChatbotLockedContent = ({
    icon,
    textContent,
    buttonText,
    button2Text,
    onButtonClick,
    button2Click,
    button2State,
    chatbotUpgradeNotice,
    }: ChatAuthorityProps) => (
    <LockBoxStyle>
        <div className="img_box">
            <img src={icon || ""} alt="lock" />
        </div>
        <div className="text">{textContent}</div>
        <div className="button" style={{ background: "#000" }} onClick={onButtonClick}>
            {buttonText}
        </div>
        {button2State && (
            <div className="button" style={{ background: "#000" }} onClick={button2Click}>
                {button2Text}
            </div>
        )}
        {chatbotUpgradeNotice && (
            <ChatbotUpgradeNotie>
                <div>※</div>
                <div>
                    VVIP Full 구독권 업그레이드시 <br />
                    AI 챗봇 이용권을 매번 결제할 필요없이 <br />
                    한달 300회씩 자유롭게 이용하실 수 있습니다. <br />
                </div>
            </ChatbotUpgradeNotie>
        )}
    </LockBoxStyle>
);

export const ErrorMessage = ({ icon, alt, textContent }: AuthorityProps) => (
    <ErrorMessageStyle>
        <div className="img_box">
            <img src={icon || ""} alt={alt || ""} width={200} height={200} />
        </div>
        <div className="text">{textContent}</div>
    </ErrorMessageStyle>
);

export const DialogContent = ({ title, description }: AuthorityProps) => (
    <DialogContentStyle>
        <p className="title">{title}</p>
        <p className="desc">{description}</p>
    </DialogContentStyle>
);

const ErrorMessageStyle = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    color: #fff;
    text-align: center;
    .img_box {
        width: 120px;
        height: 120px;
        margin-bottom: 24px;
        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }
    .text {
        font-size: 24px;
        line-height: 180%;
        .highLight {
            font-weight: 700;
        }
    }
`;

const LockBoxStyle = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    color: #fff;
    font-size: 16px;
    cursor: pointer;
    text-align: center;

    .img_box {
        width: 120px;
        height: 120px;
        overflow: hidden;
        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }
    .text {
        line-height: 28px;
        margin: 12px 0;
    }
    .button {
        padding: 12px 24px;
        font-weight: 700;
        border-radius: 8px;
        white-space: pre-line;

        font-size: 14px;
        font-weight: 700;
    }
`;

const SampleViewButton = styled.div`
    border-radius: 8px;
    border: 1px solid #2275ee;
    background: #fff;
    color: #2275ee;
    display: flex;
    padding: 12px 24px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    font-weight: 700;
`;

const NoticeStyle = styled.div`
    border-radius: 12px;
    background: linear-gradient(90deg, rgba(239, 246, 255, 0.8) 0%, rgba(245, 251, 255, 0.7) 100%);
    box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
    width: 100%;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 24px;
    color: #000; ;
`;

const FlexStyle = styled.div`
    display: flex;
    width: 100%;
    justify-content: center;
    gap: 10px;
`;

const ChatbotUpgradeNotie = styled.div`
    width: fit-content;
    display: flex;
    flex-direction: row;
    gap: 6px;

    font-size: 12px;
    font-weight: 400;
    line-height: 140%;

    div {
        text-align: left;
    }
`;

const LottieLayout = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: #fff;
    height: 100vh;
    width: 100vh;
    background-color: #fff;
    .lottie {
        width: 120px;
        height: 120px;
    }
`;

const DialogContentStyle = styled.div`
    text-align: center;
    .title {
        font-size: 16px;
        font-weight: 700;
        margin-bottom: 10px;
    }
    .desc {
        font-size: 16px;
        font-weight: 400;
        line-height: 26px;
        margin-bottom: 10px;
        white-space: pre-line;
    }
`;
